/** /auth/invite?code=... — first-time invite acceptance. */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import * as inviteApi from '@/api/invite';

const AcceptSchema = z
  .object({
    password: z.string().min(12, 'At least 12 characters'),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, { path: ['confirm'], message: 'Passwords do not match' });

type AcceptInput = z.infer<typeof AcceptSchema>;

export function InviteAcceptPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const code = params.get('code') ?? '';

  const [preview, setPreview] = useState<inviteApi.InvitePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInput>({ resolver: zodResolver(AcceptSchema) });

  useEffect(() => {
    if (!code) return;
    inviteApi
      .previewInvite(code)
      .then(setPreview)
      .catch((e: unknown) => {
        setPreviewError(
          e instanceof ConsoleApiError ? e.message : 'Couldn’t load invitation.',
        );
      });
  }, [code]);

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const r = await inviteApi.acceptInvite(code, values.password);
      navigate('/auth/mfa/enroll', {
        replace: true,
        state: { enrollmentToken: r.enrollment_token },
      });
    } catch (e) {
      setServerError(e instanceof ConsoleApiError ? e.message : 'Could not accept invite.');
    }
  });

  if (!code) {
    return (
      <Container maxWidth="xs" sx={{ pt: 8 }}>
        <Alert severity="warning">Missing invite code.</Alert>
      </Container>
    );
  }
  if (previewError) {
    return (
      <Container maxWidth="xs" sx={{ pt: 8 }}>
        <Alert severity="error">{previewError}</Alert>
      </Container>
    );
  }
  if (!preview) {
    return (
      <Container maxWidth="xs" sx={{ pt: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xs" sx={{ pt: 8 }}>
      <Typography variant="h5">Welcome, {preview.first_name || preview.email}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Set a password to activate <strong>{preview.email}</strong>.
      </Typography>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            error={Boolean(errors.password)}
            helperText={errors.password?.message ?? 'At least 12 characters'}
            {...register('password')}
          />
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            error={Boolean(errors.confirm)}
            helperText={errors.confirm?.message}
            {...register('confirm')}
          />
          {serverError && <Alert severity="error">{serverError}</Alert>}
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Continue to MFA setup'}
          </Button>
        </Stack>
      </Box>
    </Container>
  );
}
